import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { debounceTime, distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';
import { Entry, WaitListConfig, WaitListGroupStat } from '../../models';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DashboardWaitListService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  public getMoreEntries = new Subject<{
    limit: number,
    offset: number,
    searchQuery: string,
    activeOnly: boolean,
    purchaseAutoEnabled: boolean,
    categories: string[],
    debitedWithoutTicketOnly: boolean,
    sort: string,
    sortingDirection?: string
  }>();

  exportFilteredEntries(
    filters: Partial<{
      limit,
      offset,
      eventId,
      searchQuery,
      activeOnly,
      purchaseAutoEnabled,
      categories,
      debitedWithoutTicketOnly,
      sort,
      sortingDirection,
    }>,
  ): Observable<ArrayBuffer> {
    const route = `${this.configUrl}e/o/waitListEntries/${filters.eventId}`;
    const params = new HttpParams({
      fromObject: {
        format: 'csv',
        limit: filters.limit ? filters.limit.toString() : '',
        offset: filters.offset ? filters.offset.toString() : '',
        searchQuery: filters.searchQuery ? encodeURIComponent(filters.searchQuery.toString()) : '',
        activeOnly: !!filters.activeOnly,
        purchaseAutoEnabled: !!filters.purchaseAutoEnabled,
        ...filters.categories ? filters.categories?.filter( (c) => !!c)?.reduce( (acc, c, i) => ({[`categories[${i}]`]: c, ...acc}), {}) : '',
        debitedWithoutTicketOnly: !!filters.debitedWithoutTicketOnly,
        sort: filters.sort,
        sortingDirection: filters.sortingDirection || '',
      },
    });
    return this.http.get(route, {params, responseType: 'arraybuffer'}).pipe(
      map((file: ArrayBuffer) => file),
    );
  }

  getWaitListEntries(eventId: number) {
    const route = `${this.configUrl}e/o/waitListEntries/${eventId}`;
    return this.getMoreEntries.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(300),
      distinctUntilChanged(),
      // switch to new search observable each time the term changes
      switchMap( ({
        limit,
        offset,
        searchQuery,
        activeOnly,
        purchaseAutoEnabled,
        categories,
        debitedWithoutTicketOnly,
        sort,
        sortingDirection,
      }) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery.toString()),
            activeOnly: !!activeOnly,
            purchaseAutoEnabled: !!purchaseAutoEnabled,
            ...categories?.filter( (c) => !!c)?.reduce( (acc, c, i) => ({[`categories[${i}]`]: c, ...acc}), {}),
            debitedWithoutTicketOnly: !!debitedWithoutTicketOnly,
            sort,
            sortingDirection: sortingDirection || '',
          },
        });
        return this.http.get<{rows: Entry[], count: number}>(route, {params});
      }),
      map((res) => ({count: res.count, entries: res.rows})),
    );
  }

  getWaitListConfig(eventId: number) {
    const route = `${this.configUrl}e/o/waitListConfig/${eventId}`;
    return this.http.get<WaitListConfig>(route).pipe(
      first(),
    );
  }

  updateConfig(eventId: number, config) {
    const route = `${this.configUrl}e/o/waitListConfig/${eventId}`;

    return this.http.put<[WaitListConfig, boolean]>(route, config).pipe(
      first(),
    );
  }

  getNotificationPerCategory(eventId: number) {
    const route = `${this.configUrl}e/o/waitList/notificationPerCategory/${eventId}`;
    return this.http.get<{
      all: WaitListGroupStat[],
      onlyActive: WaitListGroupStat[],
    }>(route).pipe(
      first(),
      map( ({all, onlyActive}) => (all.map( ((g) => ({
        ...g,
        active: onlyActive
          .find((a) => a.categoryId === g.categoryId && a.readableGroupName === g.readableGroupName)?.groupSize,
        activeQuantity: onlyActive
          .find((a) => a.categoryId === g.categoryId && a.readableGroupName === g.readableGroupName)?.quantity,
      }))))),
    );
  }

  getCategoriesNextGroup(eventId: number) {
    const route = `${this.configUrl}e/o/waitList/categoriesNextGroup/${eventId}`;
    return this.http.get<Array<{
      categoryId: number,
      firstGroupNumber: number,
      lastGroupNumber: number,
      readableGroupName: string,
      type: string,
    }>>(route);
  }

  changeEntryGroup(entryId: number, newGroup: number, eventId: number) {
    const url = `${this.configUrl}e/o/waitList/changeEntryGroup/${eventId}`;
    return this.http.put<Entry>(url, {
      entryId,
      newGroup,
    });
  }

  updateEntryCount(entryId: number, newCount: number, eventId: number) {
    const url = `${this.configUrl}w/a/waitList/updateEntryCount/${eventId}`;
    return this.http.put<Entry>(url, {
      entryId,
      newCount,
    });
  }

}
